import {Injectable} from '@angular/core';
import {LoadScriptService} from './load-script.service';
import {HttpClient, HttpBackend} from '@angular/common/http';
import {Observable, forkJoin, of} from 'rxjs';
import {catchError, delay, map, retryWhen, take} from 'rxjs/operators';

declare var FB: Facebook;

interface Facebook {
    Event: any;

    api(url: string, callback: any): void;

    init(config: any): void;

    login(callback: any, permissions: any): void;

    getAuthResponse(config: any): void;

    getLoginStatus(config: any): void;
}

@Injectable({
    providedIn: 'root'
})
export class FacebookService {
    private httpClient: HttpClient;
    public error: any;
    public appId: string;
    public version: string;
    public isLoaded = false;
    public isInitialize = false;

    constructor(
        httpBackend: HttpBackend,
        loadScript: LoadScriptService,
    ) {
        this.httpClient = new HttpClient(httpBackend);

        loadScript.get('https://connect.facebook.net/en_US/sdk.js').subscribe(response => {
            this.isLoaded = true;
        }, error => {
            this.error = error;
        });
    }

    public initialize(appId: string, version: string = 'v6.0'): Observable<boolean> {
        this.appId = appId;
        this.version = version;

        return Observable.create(observer => {
            if (this.error) {
                observer.error(this.error);
            } else if (!this.isLoaded) {
                observer.error(new Error('Facebook SDK is not loaded'));
            } else {
                observer.next(true);
            }

            observer.complete();
        }).pipe(
            map(() => this.initializeSDK()),
            retryWhen(error => error.pipe(
                delay(200),
                take(25),
            )),
            catchError(error => of(error))
        );
    }

    public findManageablePage(): Observable<any> {
        return Observable.create(observer => {
            const loadError = this.validateSDK();
            if (loadError) {
                observer.error(loadError);
            }

            this.getAuthentification().subscribe(response => {
                if (response && response.userID) {
                    this.listAccessiblePage(response.userID).subscribe(pages => {
                        observer.next(pages);
                        observer.complete();
                    }, error => {
                        observer.error(error);
                    });
                } else {
                    observer.error(new Error('No user are connected to Facebook'));
                }
            }, error => {
                observer.error(error);
            });
        });
    }

    public askToManagePage(): Observable<any> {
        return Observable.create(observer => {
            const loadError = this.validateSDK();
            if (loadError) {
                observer.error(loadError);
            }

            this.askTokenWithScope('manage_pages, publish_pages').subscribe(response => {
                if (!response.userID) {
                    observer.error(response);
                } else {
                    this.listAccessiblePage(response.userID).subscribe(pages => {
                        observer.next(pages);
                        observer.complete();
                    }, error => {
                        observer.error(error);
                    });
                }
            }, error => {
                observer.error(error);
            });
        });
    }

    public publishToPage(pageId: string, accessToken: string, message: string, photos: string[] = null): Observable<any> {
        const body = {
            message: message,
            access_token: accessToken,
        };

        if (photos) {
            return this.postUnpublishedPhotos(pageId, accessToken, photos).pipe(
                catchError(error => {
                    return this.httpClient.post(`https://graph.facebook.com/${pageId}/feed`, body);
                }),
                map(response => {
                    response.forEach((id, index) => {
                        body[`attached_media[${index}]`] = { media_fbid: id };
                    });

                    return this.httpClient.post(`https://graph.facebook.com/${pageId}/feed`, body);
                })
            );
        } else {
            return this.httpClient.post(`https://graph.facebook.com/${pageId}/feed`, body);
        }
    }

    private postUnpublishedPhotos(pageId: string, accessToken: string, photos: string[]): Observable<any> {
        const posts = [];

        photos.forEach(photoUrl => {
            const task = this.httpClient.post(`https://graph.facebook.com/${pageId}/photos`, {
                url: photoUrl,
                caption: '',
                published: false,
                access_token: accessToken,
            }).pipe(
                map(response => response['id'])
            );

            posts.push(task);
        });

        return forkJoin(posts);
    }

    private listAccessiblePage(userId: string): Observable<any> {
        return Observable.create(observer => {
            FB.api(`/${userId}/accounts`, (response: any) => {
                if (response && !response.error) {
                    if (response.data instanceof Error) {
                        observer.error(response.data);
                    } else {
                        observer.next(response.data);
                        observer.complete();
                    }
                } else {
                    observer.error(response);
                }
            });
        });
    }

    private askTokenWithScope(scope: string): Observable<any> {
        return Observable.create(observer => {
            FB.login((response: any) => {
                if (response.authResponse) {
                    observer.next(response.authResponse);
                    observer.complete();
                } else {
                    observer.error(new Error('User cancelled login or did not fully authorize'));
                }
            }, {
                scope: scope,
            });
        });
    }

    private getAuthentification(): Observable<any> {
        return Observable.create(observer => {
            const timer = setTimeout(() => {
                observer.error(`Facebook SDK can't be used`);
            }, 2000);

            FB.getLoginStatus((response: any) => {
                clearTimeout(timer);

                if (response.status === 'connected') {
                    observer.next(response.authResponse);
                } else {
                    observer.next({});
                }

                observer.complete();
            });
        });
    }

    private initializeSDK() {
        FB.init({
            appId: this.appId,
            cookie: true,
            xfbml: true,
            version: this.version,
        });

        this.isInitialize = true;
    }

    private validateSDK() {
        if (location.protocol !== 'https:') {
            return new Error(`You can only use the Facebook SDK on HTTPS`);
        }
        if (!this.isInitialize) {
            return new Error('You need to initialize the service');
        }

        return false;
    }
}
